import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Collapse,
  Paper,
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
  Edit,
  Delete,
  Lock,
  Person,
  AccountBalance,
  TrendingUp,
  TrendingDown,
  SwapHoriz,
  Savings,
} from '@mui/icons-material';
import { Account, AccountType } from '../../services/financeService';

interface AccountTreeViewProps {
  accounts: Account[];
  onEdit?: (account: Account) => void;
  onDelete?: (account: Account) => void;
}

interface AccountNodeProps {
  account: Account;
  level: number;
  onEdit?: (account: Account) => void;
  onDelete?: (account: Account) => void;
}

const accountTypeColors: Record<AccountType, 'success' | 'error' | 'secondary' | 'info' | 'warning'> = {
  ASSET: 'success',
  LIABILITY: 'error',
  EQUITY: 'secondary',
  INCOME: 'info',
  EXPENSE: 'warning',
};

const accountTypeIcons: Record<AccountType, React.ReactNode> = {
  ASSET: <AccountBalance fontSize="small" />,
  LIABILITY: <TrendingDown fontSize="small" />,
  EQUITY: <Savings fontSize="small" />,
  INCOME: <TrendingUp fontSize="small" />,
  EXPENSE: <SwapHoriz fontSize="small" />,
};

const AccountNode: React.FC<AccountNodeProps> = ({ account, level, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(level === 0);
  const hasChildren = account.children && account.children.length > 0;

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 1,
          px: 2,
          ml: level * 3,
          mb: 0.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: level === 0 ? 'grey.50' : 'background.paper',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'primary.50',
            borderColor: 'primary.200',
            transform: 'translateX(4px)',
          },
        }}
      >
        {/* Expand/Collapse Toggle */}
        <Box sx={{ width: 28, display: 'flex', justifyContent: 'center' }}>
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ p: 0.5 }}
            >
              {expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 28 }} />
          )}
        </Box>

        {/* Account Type Icon */}
        <Tooltip title={account.account_type}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: `${accountTypeColors[account.account_type]}.100`,
              color: `${accountTypeColors[account.account_type]}.main`,
              mr: 1.5,
            }}
          >
            {accountTypeIcons[account.account_type]}
          </Box>
        </Tooltip>

        {/* Account Code */}
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 'bold',
            color: 'primary.main',
            minWidth: 100,
            mr: 2,
          }}
        >
          {account.account_code}
        </Typography>

        {/* Account Name */}
        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            fontWeight: level === 0 ? 'bold' : 'normal',
          }}
        >
          {account.account_name}
        </Typography>

        {/* Account Type Chip */}
        <Chip
          label={account.account_type}
          size="small"
          color={accountTypeColors[account.account_type]}
          sx={{ minWidth: 80, mr: 1 }}
        />

        {/* Default/Custom Badge */}
        <Tooltip title={account.is_default ? 'System Default Account' : 'Custom Account'}>
          <Chip
            icon={account.is_default ? <Lock fontSize="small" /> : <Person fontSize="small" />}
            label={account.is_default ? 'Default' : 'Custom'}
            size="small"
            variant={account.is_default ? 'outlined' : 'filled'}
            color={account.is_default ? 'default' : 'primary'}
            sx={{ minWidth: 90, mr: 1 }}
          />
        </Tooltip>

        {/* Status Chip */}
        <Chip
          label={account.is_active ? 'Active' : 'Inactive'}
          size="small"
          color={account.is_active ? 'success' : 'default'}
          variant="outlined"
          sx={{ minWidth: 70, mr: 1 }}
        />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
          {account.canEdit && onEdit && (
            <Tooltip title="Edit Account">
              <IconButton
                size="small"
                onClick={() => onEdit(account)}
                sx={{
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'primary.100' },
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {account.canDelete && onDelete && (
            <Tooltip title="Delete Account">
              <IconButton
                size="small"
                onClick={() => onDelete(account)}
                sx={{
                  color: 'error.main',
                  '&:hover': { bgcolor: 'error.100' },
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!account.canEdit && !account.canDelete && (
            <Tooltip title="Default accounts cannot be modified">
              <Box sx={{ width: 64 }} />
            </Tooltip>
          )}
        </Box>
      </Paper>

      {/* Children */}
      {hasChildren && (
        <Collapse in={expanded}>
          <Box>
            {account.children!.map((child) => (
              <AccountNode
                key={child.id}
                account={child}
                level={level + 1}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const AccountTreeView: React.FC<AccountTreeViewProps> = ({ accounts, onEdit, onDelete }) => {
  if (accounts.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No accounts found. Create a new account to get started.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      {accounts.map((account) => (
        <AccountNode
          key={account.id}
          account={account}
          level={0}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </Box>
  );
};

export default AccountTreeView;
